import React, { useState, useRef } from 'react';
import './BusinessCard.css';
import cardFrontV1 from '../images/BusinessCard_Front.png';
import cardBackV1  from '../images/BusinessCard_Back.png';
import cardFrontV2 from '../images/V2BusinessCard_Front_web.png';
import cardBackV2  from '../images/V2BusinessCard_Back_web.png';

// Front/back artwork per card version -- both share the same back layout,
// so HOTSPOTS below apply to either
const CARD_IMAGES = {
  v1: { front: cardFrontV1, back: cardBackV1 },
  v2: { front: cardFrontV2, back: cardBackV2 },
};

export const vcfContent = `BEGIN:VCARD
VERSION:3.0
FN:Dmitriy Filimonov
N:Filimonov;Dmitriy;;;
TITLE:Computer Science & Engineering
ORG:University of California, Merced
EMAIL;TYPE=INTERNET:dmitriyfilimonov33@gmail.com
TEL;TYPE=CELL:+14083728844
URL;TYPE=LinkedIn:https://linkedin.com/in/dmitriy-filimonov/
URL;TYPE=GitHub:https://github.com/dtech982
ADR;TYPE=HOME:;;Santa Clara;CA;;;US
NOTE:Solutions Engineer · Full-Stack Engineering
END:VCARD`;

export function downloadVCF() {
  const blob = new Blob([vcfContent], { type: 'text/vcard;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = 'DmitriyFilimonov.vcf';
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// Hotspots positioned as percentages of card dimensions
// Each covers the right column where contact info sits
// Top/height values tuned to the actual card back layout
const HOTSPOTS = [
  {
    id: 'email',
    // Move down 2% so y and g are fully inside
    label: 'Send Email',
    href: 'mailto:dmitriyfilimonov33@gmail.com',
    top: '17%', left: '42%', width: '55%', height: '12%',
  },
  {
    id: 'phone',
    // Box sits high -- move down 2% to center on number
    label: 'Call',
    href: 'tel:+14083728844',
    top: '31%', left: '42%', width: '55%', height: '11%',
  },
  {
    id: 'linkedin',
    // Perfect -- unchanged
    label: 'Open LinkedIn',
    href: 'https://linkedin.com/in/dmitriy-filimonov/',
    top: '44%', left: '42%', width: '55%', height: '12%',
  },
  {
    id: 'github',
    // Box sits low -- move up 2% to center on url
    label: 'Open GitHub',
    href: 'https://github.com/dtech982',
    top: '56%', left: '42%', width: '55%', height: '11%',
  },
  {
    id: 'web',
    // Box sits low -- move up 2% to center on url
    label: 'Visit Portfolio',
    href: 'https://dmitriyfilimonov.com',
    top: '69%', left: '42%', width: '55%', height: '12%',
  },
];

export default function BusinessCard({ mode = 'portfolio', version = 'v2' }) {
  const { front: cardFront, back: cardBack } = CARD_IMAGES[version] || CARD_IMAGES.v2;
  const [flipped, setFlipped]       = useState(false);
  const [activeHot, setActiveHot]   = useState(null);

  const touchStartX    = useRef(null);
  const touchStartY    = useRef(null);
  const didSwipe       = useRef(false);
  const touchStartTime = useRef(null);

  // Card click -- flip only, no download
  const handleCardClick = (e) => {
    // If a hotspot link was clicked, don't flip
    if (e.target.closest('.bc-hotspot')) return;
    if (mode === 'portfolio') return; // portfolio uses hover
    setFlipped(f => !f);
  };

  const handleTouchStart = (e) => {
    if (e.target.closest('.bc-hotspot')) return;
    touchStartX.current   = e.touches[0].clientX;
    touchStartY.current   = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    didSwipe.current      = false;
  };

  const handleTouchMove = (e) => {
    if (e.target.closest('.bc-hotspot')) return;
    if (touchStartX.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.touches[0].clientY - touchStartY.current);
    if (Math.abs(dx) > 16 && dy < 40) {
      if (!didSwipe.current) {
        didSwipe.current = true;
        setFlipped(f => !f);
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (e.target.closest('.bc-hotspot')) return;
    if (!didSwipe.current && mode === 'standalone') {
      const elapsed = Date.now() - (touchStartTime.current || 0);
      if (elapsed < 500) setFlipped(f => !f);
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <div className={`bc-wrapper bc-wrapper--${mode}`}>
      <div
        className={`bc-scene bc-scene--${mode}`}
        onMouseEnter={() => mode === 'portfolio' && setFlipped(true)}
        onMouseLeave={() => { if (mode === 'portfolio') { setFlipped(false); setActiveHot(null); } }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        aria-label="Business card, swipe to flip"
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setFlipped(f => !f)}
      >
        <div className={`bc-card ${flipped ? 'bc-card--flipped' : ''}`}>

          {/* FRONT -- no hotspots */}
          <div className="bc-face bc-face--front">
            <img src={cardFront} alt="Business Card Front" className="bc-img" />
          </div>

          {/* BACK -- hotspots overlaid on contact items */}
          <div className="bc-face bc-face--back">
            <img src={cardBack} alt="Business Card Back" className="bc-img" />

            {/* Hotspot overlay layer -- only renders on back */}
            <div className="bc-hotspots">
              {HOTSPOTS.map(spot => (
                <a
                  key={spot.id}
                  href={spot.href}
                  target={spot.id !== 'email' && spot.id !== 'phone' ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className={`bc-hotspot ${activeHot === spot.id ? 'bc-hotspot--active' : ''}`}
                  style={{
                    top:    spot.top,
                    left:   spot.left,
                    width:  spot.width,
                    height: spot.height,
                  }}
                  onMouseEnter={() => setActiveHot(spot.id)}
                  onMouseLeave={() => setActiveHot(null)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={spot.label}
                >
                  <span className="bc-hotspot__label">{spot.label} <span className="kutz-icon" aria-hidden="true">I</span></span>
                </a>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Save contact button -- below card, always visible */}
      <button
        className="bc-save-btn"
        onClick={downloadVCF}
      >
        Save Contact Details
      </button>
    </div>
  );
}